import {
  PRODUCT_CARD_BADGES_DEFAULTS,
  HOME_MERCHANDISING_TILES_DEFAULTS,
  FOOTER_POPULAR_SEARCHES_DEFAULTS,
  STOREFRONT_MERCHANDISING_DEFAULTS,
  AUTO_PRODUCT_TAGS_DEFAULTS,
} from './storefrontMerchandisingDefaults';
import { normalizeGiftTagSlug, normalizeProductGiftTags } from '../productGiftTags';

function coerceBool(val, fallback) {
  if (val === true || val === 1 || val === '1' || val === 'true') return true;
  if (val === false || val === 0 || val === '0' || val === 'false') return false;
  return fallback;
}

function clampInt(n, min, max, fallback) {
  const num = Number(n);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, Math.round(num)));
}

export function normalizeProductCardBadges(raw) {
  const d = PRODUCT_CARD_BADGES_DEFAULTS;
  const src = raw && typeof raw === 'object' ? raw : {};
  const tagSrc = Array.isArray(src.tag_badges) ? src.tag_badges : d.tag_badges;
  const tag_badges = [];
  for (let i = 0; i < 4; i++) {
    const def = d.tag_badges[i] || { tag: '', label: '' };
    const item = tagSrc[i] || {};
    const tag = normalizeGiftTagSlug(item.tag ?? def.tag);
    const label = String(item.label ?? def.label).trim();
    if (tag && label) tag_badges.push({ tag, label });
  }
  return {
    show_sale_percent: coerceBool(src.show_sale_percent, d.show_sale_percent),
    show_sold_out: coerceBool(src.show_sold_out, d.show_sold_out),
    tag_badges: tag_badges.length ? tag_badges : d.tag_badges,
  };
}

export function normalizeMerchandisingTile(item, index) {
  const d = HOME_MERCHANDISING_TILES_DEFAULTS[index] || {
    enabled: false,
    icon: '',
    title: '',
    desc: '',
    href: '',
    button_label: '',
  };
  if (!item || typeof item !== 'object') return { ...d };
  return {
    enabled: coerceBool(item.enabled, d.enabled),
    icon: String(item.icon ?? d.icon).trim() || d.icon,
    title: String(item.title ?? d.title).trim() || d.title,
    desc: String(item.desc ?? d.desc).trim() || d.desc,
    href: String(item.href ?? d.href).trim() || d.href,
    button_label: String(item.button_label ?? d.button_label).trim() || d.button_label,
  };
}

export function normalizeTestimonial(item) {
  if (!item || typeof item !== 'object') return null;
  const name = String(item.name || '').trim();
  const quote = String(item.quote || '').trim();
  if (!name || !quote) return null;
  const stars = clampInt(item.stars, 1, 5, 5);
  const enabled = coerceBool(item.enabled, true);
  if (!enabled) return null;
  return {
    enabled: true,
    name,
    quote,
    stars,
    image_url: String(item.image_url || '').trim(),
  };
}

export function normalizePopularSearch(item) {
  if (!item || typeof item !== 'object') return null;
  const label = String(item.label || '').trim();
  const href = String(item.href || '').trim();
  if (!label || !href) return null;
  return { label, href };
}

export function normalizeHomeProductShelf(item) {
  if (!item || typeof item !== 'object') return null;
  const title = String(item.title || '').trim();
  const category_slug = String(item.category_slug || '').trim();
  if (!title || !category_slug) return null;
  return {
    enabled: coerceBool(item.enabled, true),
    title,
    category_slug,
    tag_filter: normalizeGiftTagSlug(item.tag_filter || ''),
    action_label: String(item.action_label || 'View all').trim() || 'View all',
    action_href: String(item.action_href || `/shop/${category_slug}`).trim(),
    limit: clampInt(item.limit, 4, 12, 8),
  };
}

export function normalizeAutoProductTags(raw) {
  const d = AUTO_PRODUCT_TAGS_DEFAULTS;
  const src = raw && typeof raw === 'object' ? raw : {};
  const newSince = String(src.new_since ?? d.new_since).trim();
  return {
    enabled: coerceBool(src.enabled, d.enabled),
    new_enabled: coerceBool(src.new_enabled, d.new_enabled),
    new_days: clampInt(src.new_days, 7, 120, d.new_days),
    new_since: /^\d{4}-\d{2}-\d{2}$/.test(newSince) ? newSince : d.new_since,
    bestseller_enabled: coerceBool(src.bestseller_enabled, d.bestseller_enabled),
    bestseller_days: clampInt(src.bestseller_days, 7, 90, d.bestseller_days),
    bestseller_limit: clampInt(src.bestseller_limit, 3, 20, d.bestseller_limit),
    bestseller_min_views: clampInt(src.bestseller_min_views, 0, 100, d.bestseller_min_views),
    bestseller_min_units: clampInt(src.bestseller_min_units, 0, 50, d.bestseller_min_units),
    views_weight: clampInt(src.views_weight, 0, 20, d.views_weight),
    sales_weight: clampInt(src.sales_weight, 0, 20, d.sales_weight),
  };
}

export function mergeStorefrontMerchandising(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const d = STOREFRONT_MERCHANDISING_DEFAULTS;
  const tiles = Array.isArray(src.home_merchandising_tiles) ? src.home_merchandising_tiles : [];
  const testimonials = Array.isArray(src.home_testimonials) ? src.home_testimonials : [];
  const searches = Array.isArray(src.footer_popular_searches) ? src.footer_popular_searches : [];
  const shelves = Array.isArray(src.home_product_shelves) ? src.home_product_shelves : [];

  return {
    product_card_badges: normalizeProductCardBadges(src.product_card_badges),
    home_trust_strip_enabled: coerceBool(src.home_trust_strip_enabled, d.home_trust_strip_enabled),
    trust_badge_4: String(src.trust_badge_4 ?? d.trust_badge_4).trim(),
    trust_stat_line: String(src.trust_stat_line ?? d.trust_stat_line).trim(),
    home_merchandising_enabled: coerceBool(src.home_merchandising_enabled, d.home_merchandising_enabled),
    home_merchandising_tiles: [0, 1, 2].map((i) => normalizeMerchandisingTile(tiles[i], i)),
    home_testimonials_enabled: coerceBool(src.home_testimonials_enabled, d.home_testimonials_enabled),
    home_testimonials_title:
      String(src.home_testimonials_title ?? d.home_testimonials_title).trim() || d.home_testimonials_title,
    home_testimonials: testimonials.map(normalizeTestimonial).filter(Boolean).slice(0, 8),
    footer_popular_searches_enabled: coerceBool(
      src.footer_popular_searches_enabled,
      d.footer_popular_searches_enabled,
    ),
    footer_popular_searches_title:
      String(src.footer_popular_searches_title ?? d.footer_popular_searches_title).trim() ||
      d.footer_popular_searches_title,
    footer_popular_searches: (searches.length
      ? searches.map(normalizePopularSearch).filter(Boolean)
      : FOOTER_POPULAR_SEARCHES_DEFAULTS
    ).slice(0, 20),
    home_product_shelves: shelves.map(normalizeHomeProductShelf).filter(Boolean).slice(0, 4),
    auto_product_tags: normalizeAutoProductTags(src.auto_product_tags),
  };
}
